import React, { useState, useEffect } from 'react';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { facebookService } from '../../services/api';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';

export default function FacebookWarningBanner({ userRole }) {
  const { t } = useLanguage();
  const [fbStatus, setFbStatus] = useState({ loading: true, connected: true, isExpired: false, serverError: false });

  useEffect(() => {
    let mounted = true;
    
    const checkStatus = async () => {
      try {
        const data = await facebookService.getStatus();
        if (mounted) {
          // Show banner if explicitly disconnected or if token is expired
          setFbStatus({ 
            loading: false, 
            connected: data.connected !== false, 
            isExpired: data.isExpired === true,
            serverError: false 
          });
        }
      } catch (error) {
        if (mounted) {
          // If it's a server error (500, network issue etc.) — don't show disconnected warning
          // Only show if it's a clear "not configured" or auth error
          const isServerError = error.message?.includes('500') || 
                                error.message?.includes('Internal') ||
                                error.name === 'TypeError'; // network error
          setFbStatus({ 
            loading: false, 
            connected: true,  // assume connected on server errors — don't false alarm
            isExpired: false,
            serverError: isServerError 
          });
        }
      }
    };

    checkStatus();
    
    // Check every 5 minutes
    const interval = setInterval(checkStatus, 5 * 60 * 1000);
    
    // Listen for manual connect/disconnect events
    window.addEventListener('fb_status_changed', checkStatus);

    return () => {
      mounted = false;
      clearInterval(interval);
      window.removeEventListener('fb_status_changed', checkStatus);
    };
  }, []);

  // Don't show banner while loading, or when there's a server error.
  // Show it ONLY if not connected, or if token is expired.
  if (fbStatus.loading || fbStatus.serverError || (fbStatus.connected && !fbStatus.isExpired)) {
    return null;
  }

  const isAdmin = userRole === 'Admin' || userRole === 'Owner';

  return (
    <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 md:p-4 bg-red-50 border border-red-300 rounded-xl shadow-sm animate-pulse-once">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
        <p className="text-sm font-medium text-red-800">
          <strong>{t('fbDisconnectedTitle') || 'Facebook Leads Disconnected.'}</strong>{' '}
          {isAdmin 
            ? t('fbDisconnectedAdminMsg') || "Your Meta App token has expired or is disconnected." 
            : t('fbDisconnectedSalesMsg') || "The system cannot pull new leads from Facebook. Please contact the administrator."}
        </p>
      </div>
      
      {isAdmin && (
        <Link 
          to="/integrations" 
          className="shrink-0 flex items-center justify-center px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-bold rounded-lg transition-colors shadow-sm shadow-red-200 whitespace-nowrap"
        >
          {t('reconnectNow') || 'Reconnect Now'}
        </Link>
      )}
    </div>
  );
}
